// Typeable combobox: a single-value TomSelect that works like a text field that is also a
// dropdown (theme/_components.scss "Typeable combobox"). Shared by the order form's product
// field (orders.js initProductSelect) and the contact form's company type (contacts.js
// initContactTypeField):
// - the fx_typeable TomSelect plugin (typing, focus / blur, the chevron, Enter / Tab / Escape,
//   keeping the list in view on desktop and above a phone's keyboard, a11y);
// - search helpers (typeableScore: match anywhere, ignoring case and accents);
// - the field's help line, inline error and ARIA state (addTypeableFieldParts,
//   refreshTypeableField; held back while a click is in progress: afterPointerRelease);
// - saving (initTypeableFormSubmit: a blocked save focuses the field instead of the browser
//   bubble; a valid save is sent once).
import TomSelect from 'tom-select';

export const narrowScreen = window.matchMedia('(max-width: 575.98px)');
export const coarsePointer = window.matchMedia('(pointer: coarse)');

export function nameKey(name) {
    return String(name ?? '').trim().toLowerCase();
}

// Search key: lower case, accents dropped ("Café" is found by "cafe")
export function foldText(text) {
    return String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// The option labelled `name` ignoring case
export function findOptionByLabel(tomSelect, name) {
    const key = nameKey(name);
    if (!key) {
        return null;
    }

    const field = tomSelect.settings.labelField;
    return Object.values(tomSelect.options).find(option => nameKey(option[field]) === key) || null;
}

// Characters, as the server counts them (mb_strlen): an emoji is one, not two UTF-16 units
export function charCount(text) {
    return [...String(text ?? '')].length;
}

export function shorten(text, length = 40) {
    const chars = [...String(text ?? '')];
    return chars.length > length ? `${chars.slice(0, length - 1).join('')}…` : chars.join('');
}

// A mouse button (or a tap's synthetic mouse press) is down somewhere on the page. Leaving a
// field with a click elsewhere (e.g. on Save) blurs it on mousedown: anything that changes the
// layout then (a help line appearing) moves the button away before mouseup, and the click is
// lost. afterPointerRelease runs such changes once the press is over, after its click.
let pointerHeld = false;
let releaseQueue = [];
function releasePointer() {
    if (!pointerHeld) {
        return;
    }
    pointerHeld = false;
    const queued = releaseQueue;
    releaseQueue = [];
    if (queued.length) {
        // after the click that follows mouseup (same task), and anything it triggers
        setTimeout(() => queued.forEach(run => run()), 0);
    }
}
document.addEventListener('mousedown', () => { pointerHeld = true; }, true);
document.addEventListener('mouseup', releasePointer, true);
document.addEventListener('pointercancel', releasePointer, true);
document.addEventListener('dragend', releasePointer, true);
document.addEventListener('mousemove', (event) => {
    if (pointerHeld && event.buttons === 0) {
        releasePointer();
    }
}, true);
window.addEventListener('blur', releasePointer);

export function afterPointerRelease(run) {
    if (pointerHeld) {
        releaseQueue.push(run);
    } else {
        run();
    }
}

// TomSelect `score` setting: match anywhere in the label (option.text), ignoring case and
// accents: every word typed must appear, labels starting with it first. Own scoring instead of
// TomSelect's accent-aware patterns (diacritics: false), whose cost grows exponentially with
// repeated letters ("mmmm…" froze the page).
export function typeableScore(search) {
    const words = foldText(search).split(/\s+/).filter(Boolean);
    return (option) => {
        if (!words.length) {
            return 1;
        }
        const name = foldText(option.text);
        let score = 0;
        for (const word of words) {
            const at = name.indexOf(word);
            if (at < 0) {
                return 0;
            }
            score += at === 0 ? 3 : (/[\s\-\/(]/.test(name[at - 1]) ? 2 : 1);
        }
        return score / words.length;
    };
}

// TomSelect plugin: a single-value field that works like a text input instead of a select.
// Instance flags it keeps for the page: fxPending (text left in the field without a choice),
// fxLastValue (the last value chosen) and fxSelectVia ('tab' | 'pointer' | 'key' while a
// suggestion or the Add row is being taken). Events: fx_pending (fxPending changed), fx_text
// (Escape changed the text without a change of value).
// Options (plugins: [{ name: 'fx_typeable', options: { ... } }]):
// - heading: the line over the full list of an empty field ('All products · type to filter'),
//   or a function (tomSelect) => that line;
// - findOption(tomSelect, text): the option that typed text names (default findOptionByLabel);
// - browseFilled: a field that holds a value also lists every option on focus / click (the
//   current one highlighted), instead of only an empty field;
// - chevron: the select chevron on the right (theme .fx-combobox--chevron); a press on it
//   toggles the list (the full list, also for a field holding a value);
// - pendingOverValue: leaving a field that holds a value with text typed over it that names no
//   option keeps the text as not chosen yet (the value goes, so a required field blocks the
//   save) instead of falling back to the value; Escape still brings the value back.
if (!(TomSelect.plugins && TomSelect.plugins.fx_typeable)) {
    TomSelect.define('fx_typeable', function (pluginOptions) {
        const self = this;
        const valueField = () => self.settings.valueField;
        const settings = pluginOptions || {};
        const headingText = () => {
            if (typeof settings.heading === 'function') {
                return settings.heading(self);
            }
            return typeof settings.heading === 'string' ? settings.heading : 'All options · type to filter';
        };
        const findOption = typeof settings.findOption === 'function' ? settings.findOption : findOptionByLabel;
        const browseFilled = Boolean(settings.browseFilled);
        const pendingOverValue = Boolean(settings.pendingOverValue);
        const chevron = Boolean(settings.chevron);

        self.fxPending = false;
        self.fxLastValue = '';
        self.fxSelectVia = null;

        const setPending = (pending) => {
            if (self.fxPending !== pending) {
                self.fxPending = pending;
                self.wrapper.classList.toggle('fx-combobox--pending', pending);
                self.trigger('fx_pending', pending);
            }
        };

        // Typing replaces the shown value, so the value hides while the field holds text
        const syncTyping = () => {
            self.wrapper.classList.toggle('fx-combobox--typing', self.control_input.value !== '');
        };

        // Lists the options: text in the field shows its suggestions; an empty field the full
        // list (the hybrid part: also how a phone browses); a field holding a value only with
        // browseFilled or `all` (the chevron)
        const showList = (all = false) => {
            if (self.isOpen || self.isDisabled || self.isReadOnly) {
                return;
            }
            if (self.inputValue() !== '') {
                self.refreshOptions(true);
                return;
            }
            if (self.items.length && !browseFilled && !all) {
                return;
            }
            if (Object.keys(self.options).length) {
                self.refreshOptions(true);
            }
        };

        // A press on the chevron (the control's right padding, theme "Typeable combobox")
        // toggles the list; it is noted on mousedown, before the press can change it
        let chevronPress = null;
        const onChevron = (event) => {
            if (!chevron) {
                return false;
            }
            const rect = self.control.getBoundingClientRect();
            const zone = parseFloat(getComputedStyle(self.control).paddingRight) || 0;
            return zone > 0 && event.clientX >= rect.right - zone && event.clientX <= rect.right;
        };

        // A click on the control. It puts the caret in the field: it never turns the value into
        // a selected chip (which blocks typing) and never closes the field like a select
        // toggle (only the chevron does). A typed new value opens as text, so a typo is fixed
        // in place (Enter or Tab confirms it again; Escape keeps the value it had); the chevron
        // lists every option instead.
        const controlClick = () => {
            const press = chevronPress;
            chevronPress = null;
            if (self.isDisabled || self.isReadOnly) {
                return;
            }
            if (press === 'close') {
                self.close(false); // keeps the focus and any text
                return;
            }
            const value = self.getValue();
            const option = value ? self.options[value] : null;
            if (option && option.isNew && self.control_input.value === '' && press !== 'open') {
                self.setTextboxValue(option[self.settings.labelField]);
                self.removeItem(value);
            }
            if (self.isFocused) {
                showList(press === 'open');
                return;
            }
            self.fxShowAll = press === 'open';
            self.focus(); // onFocus lists the options
            if (self.control_input.value !== '') {
                const end = self.control_input.value.length;
                self.control_input.setSelectionRange(end, end);
            }
        };

        self.on('initialize', () => {
            // Options read from a <select> come numbered by TomSelect's settings parser, not
            // by the instance's counter: an option added later (a typed new value) must still
            // sort after them and get an element id of its own (not "-opt-1" again)
            Object.values(self.options).forEach(option => {
                self.order = Math.max(self.order, option.$order || 0);
            });
            self.wrapper.classList.toggle('fx-combobox--chevron', chevron);
            self.control_input.addEventListener('input', syncTyping);
            self.fxLastValue = self.getValue();
            self.control.addEventListener('mousedown', (event) => {
                chevronPress = event.button === 0 && onChevron(event) ? (self.isOpen ? 'close' : 'open') : null;
            }, true);
            // TomSelect leaves a click on a field holding text to the browser (which its
            // document mousedown handler blocks): handled here (capture: before TomSelect's
            // own handler, which calls onClick for an empty field)
            self.control.addEventListener('click', () => {
                if (self.control_input.value !== '') {
                    controlClick();
                }
            }, true);
        });
        self.hook('after', 'setTextboxValue', syncTyping);

        // The last value chosen, for Escape (and leaving the field) to fall back on
        self.on('change', (value) => {
            if (value) {
                self.fxLastValue = value;
                setPending(false);
            }
        });

        // aria-activedescendant only while the list is open (TomSelect leaves it pointing
        // into a closed list)
        const syncActiveDescendant = () => {
            if (self.isOpen && self.activeOption && self.activeOption.id) {
                self.focus_node.setAttribute('aria-activedescendant', self.activeOption.id);
            } else {
                self.focus_node.removeAttribute('aria-activedescendant');
            }
        };
        self.hook('after', 'setActiveOption', syncActiveDescendant);
        self.hook('after', 'close', syncActiveDescendant);

        // Keep the open list in view.
        // Desktop: the nearest scroll that shows it (the page's scroll-padding keeps it
        // clear of a sticky save bar); instant, because a smooth scroll (Bootstrap reboot)
        // is cut short by the browser's own keep-the-caret-visible scroll on the next
        // keystroke. Only while the field has focus: never while it is blurring (a click
        // elsewhere, e.g. on Save, must not see the page jump between mousedown and mouseup).
        // The list scrolling (or re-rendering) under a mouse that does not move must not
        // highlight the row that lands under it (Enter would pick it): hover highlights again
        // once the mouse moves over the list (TomSelect resets ignoreHover on mousemove).
        // Touch screens: the soft keyboard covers the lower part of the screen without
        // shrinking the layout viewport, so when the list opens the field moves near the
        // top and the list is sized to what stays visible above the keyboard (visualViewport).
        const fitToVisibleArea = () => {
            if (!self.isOpen) {
                return;
            }
            const viewport = window.visualViewport;
            const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
            const room = Math.floor(visibleBottom - self.dropdown_content.getBoundingClientRect().top - 12);
            self.dropdown_content.style.maxHeight = `${Math.max(132, Math.min(320, room))}px`;
        };
        const revealDropdown = () => {
            if (!self.isOpen || !self.isFocused) {
                return;
            }
            if (coarsePointer.matches) {
                fitToVisibleArea();
            } else {
                self.ignoreHover = true;
                self.dropdown.scrollIntoView({ block: 'nearest', behavior: 'instant' });
            }
        };
        self.on('dropdown_open', () => {
            syncActiveDescendant();
            if (coarsePointer.matches && self.isFocused) {
                self.wrapper.scrollIntoView({ block: 'start', behavior: 'instant' });
                if (window.visualViewport) {
                    window.visualViewport.addEventListener('resize', fitToVisibleArea);
                }
            }
            revealDropdown();
        });
        self.on('dropdown_close', () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', fitToVisibleArea);
            }
        });

        self.hook('instead', 'onItemSelect', () => false);
        self.hook('instead', 'onClick', controlClick);

        // Focus: an empty field lists the catalog (with browseFilled, a filled one too); text in
        // it (left unchosen, or a typed new value opened as text) shows its suggestions again.
        // fxQuietFocus (a save attempt moving to the field) only focuses.
        self.hook('after', 'onFocus', () => {
            if (!self.isFocused || self.isOpen) {
                return;
            }
            const quiet = self.fxQuietFocus;
            const all = self.fxShowAll;
            self.fxQuietFocus = false;
            self.fxShowAll = false;
            setPending(false);
            if (!quiet) {
                showList(all);
            }
        });

        const tsKeyDown = self.onKeyDown;
        self.hook('instead', 'onKeyDown', (event) => {
            const text = self.inputValue();

            // Arrows move into a list that has no highlighted row yet (the catalog list)
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && self.isOpen && !self.activeOption) {
                const rows = self.selectable();
                if (rows.length) {
                    self.setActiveOption(event.key === 'ArrowDown' ? rows[0] : rows[rows.length - 1]);
                }
                event.preventDefault();
                return;
            }

            // Enter with the list closed never submits the form from this field: an exact
            // name takes that option, other text shows its suggestions
            if (event.key === 'Enter' && !self.isOpen && text !== '') {
                event.preventDefault();
                const match = findOption(self, text);
                if (match) {
                    self.fxSelectVia = 'key';
                    self.setTextboxValue('');
                    self.addItem(match[valueField()]);
                    self.fxSelectVia = null;
                } else {
                    self.refreshOptions(true);
                }
                return;
            }

            // Tab (once something was typed) takes the highlighted suggestion or Add row,
            // then focus moves on as usual
            if (event.key === 'Tab' && self.isOpen && text !== '' && self.canSelect(self.activeOption)) {
                self.fxSelectVia = 'tab';
                self.onOptionSelect(event, self.activeOption);
                self.fxSelectVia = null;
            }

            // Escape cancels the edit: the typed text goes, and a value that Backspace (or
            // a click on a new name, or text left over it) turned into text comes back
            if (event.key === 'Escape') {
                if (!self.isOpen && self.control_input.value !== '') {
                    self.setTextboxValue('');
                }
                if (!self.getValue() && self.fxLastValue && self.options[self.fxLastValue]) {
                    self.setTextboxValue('');
                    self.addItem(self.fxLastValue);
                }
                setPending(false);
                const result = tsKeyDown.call(self, event);
                self.trigger('fx_text', self.inputValue()); // e.g. an error quoting the text
                return result;
            }

            return tsKeyDown.call(self, event);
        });

        // How a suggestion or the Add row was taken (the page moves focus accordingly)
        self.hook('instead', 'onOptionSelect', ((tsOptionSelect) => (event, option) => {
            if (!self.fxSelectVia) {
                self.fxSelectVia = event && /click|mouse|pointer|touch/.test(event.type || '') ? 'pointer' : 'key';
            }
            try {
                return tsOptionSelect.call(self, event, option);
            } finally {
                self.fxSelectVia = null;
            }
        })(self.onOptionSelect));

        // Leaving the field never picks or creates an option:
        // - text equal to an option's name (ignoring case) is that option;
        // - with pendingOverValue, any other text stays, marked as not chosen yet (fxPending),
        //   and a value it was typed over goes (Escape brings it back), so the save asks for a
        //   choice instead of keeping the old value without a word;
        // - otherwise a field that had a value keeps it (as Escape), and on an empty field the
        //   text stays, marked as not chosen yet
        let leftText = null;
        self.hook('before', 'onBlur', () => {
            leftText = null;
            if (document.hasFocus() === false || !self.isFocused) {
                return;
            }

            const text = self.inputValue();
            if (text === '') {
                if (!self.getValue()) {
                    self.fxLastValue = ''; // the field was cleared on purpose
                }
                return;
            }

            const match = findOption(self, text);
            if (match) {
                self.setTextboxValue('');
                self.addItem(match[valueField()]);
                return;
            }
            if (pendingOverValue) {
                leftText = self.control_input.value;
                if (self.getValue()) {
                    self.removeItem(self.getValue());
                }
                return;
            }
            if (!self.getValue() && self.fxLastValue && self.options[self.fxLastValue]) {
                self.setTextboxValue('');
                self.addItem(self.fxLastValue);
                return;
            }
            if (!self.getValue()) {
                leftText = self.control_input.value;
            }
        });
        self.hook('after', 'onBlur', () => {
            if (leftText !== null && !self.isFocused) {
                const text = leftText;
                leftText = null;
                self.setTextboxValue(text);
                setPending(true);
            }
        });

        // After each refresh: the "Add ..." row goes after the matches; only option names
        // keep the match highlight (not prices, the Add row or messages); the full list of an
        // empty field gets a heading and no preselected row (Enter does not pick the first
        // option by accident)
        self.hook('after', 'refreshOptions', () => {
            const content = self.dropdown_content;
            const create = content.querySelector(':scope > .create');
            if (create && create !== content.lastElementChild) {
                content.appendChild(create);
            }

            content.querySelectorAll('.highlight').forEach(mark => {
                if (!mark.closest('.fx-combobox__label')) {
                    const parent = mark.parentNode;
                    mark.replaceWith(...mark.childNodes);
                    parent.normalize();
                }
            });

            if (self.inputValue() === '' && content.querySelector('[data-selectable]')) {
                const headingRow = document.createElement('div');
                headingRow.className = 'fx-combobox__heading';
                headingRow.setAttribute('role', 'presentation');
                headingRow.textContent = headingText();
                content.prepend(headingRow);
                if (!self.items.length) {
                    self.clearActiveOption();
                }
            }

            // the list changes size as the text changes
            revealDropdown();
        });
    });
}

// The field's screen-reader value and help line (under the field), and a server error printed
// for it (failed save), adopted as the field's error until it holds a value
export function addTypeableFieldParts(tomSelect, serverError = null) {
    const valueText = document.createElement('span');
    valueText.className = 'visually-hidden fx-combobox-value';
    valueText.id = `${tomSelect.inputId}-value`;
    const help = document.createElement('div');
    help.className = 'fx-combobox-help';
    help.id = `${tomSelect.inputId}-help`;
    help.hidden = true;
    tomSelect.wrapper.after(valueText, help);

    if (serverError) {
        serverError.id = `${tomSelect.inputId}-error`;
        serverError.classList.add('fx-combobox-error');
        tomSelect.wrapper.classList.add('fx-combobox--invalid');
    }
}

// Validity (the save checks it), the inline error and help line, and the ARIA state of a
// typeable field.
// - problem: what the field still needs ('' when it holds a value);
// - fromSaveAttempt: the save was blocked by this field;
// - describe(invalid): { help, tone ('note' | 'warn'), value } for the help line and the
//   screen-reader value;
// - errorClass: the class of an error it adds (the form's validation text style).
// The validity is set at once. While a mouse press is in progress (leaving the field with a
// click on Save blurs it on mousedown) the help line and error wait until the press is over,
// so the page does not move under the pointer and the click lands where it was aimed.
export function refreshTypeableField(tomSelect, options) {
    tomSelect.input.setCustomValidity(options.problem);
    tomSelect.fxRefreshWith = tomSelect.fxRefreshQueued
        ? { ...options, fromSaveAttempt: options.fromSaveAttempt || tomSelect.fxRefreshWith.fromSaveAttempt }
        : options;
    if (tomSelect.fxRefreshQueued) {
        return;
    }
    tomSelect.fxRefreshQueued = true;
    afterPointerRelease(() => {
        tomSelect.fxRefreshQueued = false;
        renderTypeableField(tomSelect, tomSelect.fxRefreshWith);
    });
}

function renderTypeableField(tomSelect, { problem, fromSaveAttempt = false, describe, errorClass = 'error-message' }) {
    const id = tomSelect.inputId;
    const control = tomSelect.control_input;

    // Inline error: after a blocked save (or from the server) until the field is complete
    let error = document.getElementById(`${id}-error`);
    if (!problem) {
        tomSelect.wrapper.classList.remove('fx-combobox--invalid');
        if (error) {
            error.remove();
            error = null;
        }
    } else if (tomSelect.wrapper.classList.contains('fx-combobox--invalid')) {
        if (!error) {
            error = document.createElement('div');
            error.className = `${errorClass} fx-combobox-error`;
            error.id = `${id}-error`;
            const help = document.getElementById(`${id}-help`);
            (help || tomSelect.wrapper).after(error);
            fromSaveAttempt = true;
        }
        if (fromSaveAttempt || error.dataset.client === '1') {
            error.dataset.client = '1';
            error.textContent = problem;
        }
    }
    const invalid = Boolean(error);
    const { help: message = '', tone = 'note', value: valueLabel = '' } = describe(invalid) || {};

    const help = document.getElementById(`${id}-help`);
    if (help) {
        help.textContent = message;
        help.hidden = message === '';
        help.classList.toggle('fx-combobox-help--warn', tone === 'warn');
    }

    // Screen readers: the value (not in the text box itself), help and error
    const valueText = document.getElementById(`${id}-value`);
    if (valueText) {
        valueText.textContent = valueLabel;
    }
    const describedBy = [`${id}-value`, `${id}-help`, `${id}-error`].filter(part => {
        const element = document.getElementById(part);
        return element && !element.hidden && element.textContent !== '';
    });
    if (describedBy.length) {
        control.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
        control.removeAttribute('aria-describedby');
    }
    if (invalid) {
        control.setAttribute('aria-invalid', 'true');
    } else {
        control.removeAttribute('aria-invalid');
    }
}

// Saving: a typeable field (fieldSelector: its <select>) that blocks the save gets an inline
// message and the focus (not the browser bubble pointing at the hidden <select>); a valid save
// is sent once.
export function initTypeableFormSubmit(form, fieldSelector) {
    let attempt = null;
    form.addEventListener('invalid', (event) => {
        const field = event.target;
        if (!attempt) {
            const first = [...form.elements].find(element => element.willValidate && !element.validity.valid) || null;
            attempt = { first, fieldFirst: Boolean(first && first.matches(fieldSelector)) };
            setTimeout(() => {
                const done = attempt;
                attempt = null;
                const tomSelect = done.fieldFirst ? done.first.tomselect : null;
                if (tomSelect) {
                    tomSelect.fxQuietFocus = true;
                    tomSelect.focus();
                    tomSelect.wrapper.scrollIntoView({ block: 'center', behavior: 'instant' });
                }
            }, 0);
        }
        // A typeable field reports itself inline; when it is the first problem, the browser
        // shows nothing (the focus goes to the field)
        if (field.matches(fieldSelector) || attempt.fieldFirst) {
            event.preventDefault();
        }
    }, true);

    const saveButtons = () => form.querySelectorAll('button[type="submit"], input[type="submit"]');
    form.addEventListener('submit', (event) => {
        if (form.dataset.fxSubmitting === '1') {
            event.preventDefault();
            return;
        }
        form.dataset.fxSubmitting = '1';
        saveButtons().forEach(button => button.classList.add('disabled'));
        setTimeout(() => {
            if (event.defaultPrevented) {
                delete form.dataset.fxSubmitting;
                saveButtons().forEach(button => button.classList.remove('disabled'));
            }
        }, 0);
    });
    // Back to the page from the history cache: it can be saved again
    window.addEventListener('pageshow', () => {
        delete form.dataset.fxSubmitting;
        saveButtons().forEach(button => button.classList.remove('disabled'));
    });
}
